// The account pieces (header controls, account page, bug report page,
// settings dialog), built by kast-common's account kit around this site's
// session and sign-in. The page around the account and bug report pages is
// in pages/.

import { useLocation } from "react-router-dom";
import { createAccountKit } from "@jkast90/kast-common/account";
import { hasGroup, useSession } from "@jkast90/kast-common/auth";
import { LegalLinks } from "@jkast90/kast-common/legal/react";
import { buttonClass } from "@jkast90/kast-common/ui";
import styles from "../pages/ContentPage.module.css";
import { isLocalDev } from "./auth/isLocalDev";
import { HNT_ADMIN_GROUPS, session, signIn, signOut } from "./auth/session";
import { deploymentConfig } from "./deployment-config";
import { themeStore } from "./theme";

const CLASSES = {
  title: styles.title,
  sectionTitle: styles.subhead,
  button: buttonClass({ variant: "secondary" }),
  primaryButton: buttonClass({ variant: "primary" }),
  // The shared field styles (ui.css); the form's textarea and select also need room inside.
  input: `kui-input ${styles.formControl}`,
  label: "kui-field-label",
  hint: "kui-field-hint",
};

const isOrganizer = (state: Parameters<typeof hasGroup>[0]) => hasGroup(state, HNT_ADMIN_GROUPS, { ignoreCase: true });

/**
 * The organizer groups decide the Admin link (and local dev, which takes no
 * tokens, shows it to everyone): `isAdmin` says whether this is an organizer,
 * `unlocked` whether the link shows. There is no admin-tools switch here
 * (`adminSwitch: false`), so `setEnabled` has nothing to set. The panel checks
 * the groups again, and so does the API.
 */
function useOrganizerAdmin() {
  const isAdmin = isOrganizer(useSession(session));
  return { isAdmin, unlocked: isAdmin || isLocalDev(), setEnabled: () => {} };
}

export const { AccountControls, AccountPage, BugReportPage, AppSettings } = createAccountKit({
  session,
  signIn: () => signIn(),
  signOut: () => void signOut(),
  themeStore,
  admin: useOrganizerAdmin,
  adminSwitch: false,
  apiUrl: () => deploymentConfig.get("apiUrl"),
  // The site never advertises sign-in: visitors get the Settings gear alone.
  signedOutControls: "settings",
  useLocationState: () => useLocation().state,
  // Reports go to the kast-dev admin inbox, not this site's API.
  bugReport: { app: "hashers_not_trashers", showLogs: isOrganizer, classNames: CLASSES },
  settings: { legal: <LegalLinks newTab={false} /> },
});
